import { Type } from "@earendil-works/pi-ai";

import { renderGraph } from "./investigationGraph.js";
import { SEARCH_DEFAULT_WINDOW_SECONDS, searchSpec, termFieldFor } from "./graphPivot.js";
import { graphSnapshot, rowsFromContent } from "./investigationGraphRun.js";
import { rowExcerpt } from "./graphExpand.js";

// A recipe is a named plan the harness runs end to end: the queries a
// successful run made, in order, with the literals that came from the
// question turned into parameters and the literals that came from an
// earlier step's rows turned into bindings, then expand over the anchors
// the plan produced. The model names the recipe and fills its parameters
// from the question; it never writes the queries. Recipes are distilled
// from runs that ended in supported claims, replayed before they are
// stored, and gated by the store.
export const RECIPE_STEP_KINDS = Object.freeze(["search", "query", "expand"]);
// Bumped when what a stored recipe means changes (its origin cost is now
// every tool call of the learning turn); recipes learned before retire.
export const RECIPE_VERSION = 2;
// The tools that read a trace without going through expand; a run that
// answered through them has no anchor for the distiller.
const DIRECT_READ_TOOLS = /^(trace_reading|trace_turn|trace_lane|trace_lanes|trace_overview|read_chat_transcript|read_tool_call)$/;
export const MAX_EXPAND_PER_RECIPE = 3;
export const MAX_SEARCH_ANCHORS = 8;
// A binding substitutes at most this many distinct values into an IN list.
export const MAX_BOUND_VALUES = 50;
// How much of a step's result the distiller indexes when it looks for
// where a later literal came from.
const MAX_INDEXED_ROWS = 500;
const MAX_INDEXED_CELLS = 6000;
// A spec larger than this is not kept in the trace (nor distilled).
const MAX_TRACED_SPEC_CHARS = 12000;
// How many example questions a recipe keeps, and how long a run of words a
// trigger may share with one of them before it counts as a restatement.
export const MAX_EXAMPLES = 3;
const SHARED_PHRASE_WORDS = 5;

const WHOLE_PARAM = /^\{\{(\w+)\}\}$/;
const WHOLE_BINDING = /^\{\{s(\d+)\.([A-Za-z0-9_]+)\}\}$/;
const ANY_REF = /\{\{(s\d+\.[A-Za-z0-9_]+|\w+)\}\}/g;

// Fill a step field from the run's parameters. A field that is exactly one
// parameter keeps the parameter's type (a window object stays an object);
// anything else is string substitution.
export function fillTemplate(value, params = {}, question = null) {
    if (typeof value !== "string") return value;
    const lookup = (name) => (name === "question" ? (params.question ?? question ?? null) : (params[name] ?? null));
    const whole = WHOLE_PARAM.exec(value.trim());
    if (whole) return lookup(whole[1]);
    return value.replace(/\{\{(\w+)\}\}/g, (_, name) => { const v = lookup(name); return v == null ? "" : (typeof v === "string" ? v : JSON.stringify(v)); });
}

// ── The plan's shape ─────────────────────────────────────────────────

export function stepEntity(step) {
    if (!step) return null;
    if (step.kind === "search") return step.entity || null;
    if (step.kind === "query") return String(step.spec?.root?.entity || "").replace(/^ENTITY_TYPE_/, "") || null;
    return null;
}

// Same query path (entities in order) and same anchor kind means the same
// recipe.
export function recipeSignature(recipe) {
    const path = (recipe.steps || []).filter((s) => s.kind === "search" || s.kind === "query").map((s) => stepEntity(s) || "-").join(">");
    const expand = (recipe.steps || []).find((s) => s.kind === "expand");
    // A plan that ends in rows (no expand) is its own plan even over the same
    // entities: it yields anchors of a kind for the model to read.
    const tail = expand ? (expand.anchors?.kind || "-") : (recipe.yields?.kind ? `rows:${recipe.yields.kind}` : "-");
    return `${path || "-"}|${tail}`;
}

function refLabel(value) {
    if (typeof value !== "string") return null;
    const whole = WHOLE_BINDING.exec(value.trim());
    if (whole) return `${whole[2]} from step ${whole[1]}`;
    const param = WHOLE_PARAM.exec(value.trim());
    if (param) return `the ${param[1]}`;
    const inner = /\{\{s(\d+)\.([A-Za-z0-9_]+)\}\}/.exec(value);
    if (inner) return `${inner[2]} from step ${inner[1]}`;
    const p = /\{\{(\w+)\}\}/.exec(value);
    if (p) return `the ${p[1]}`;
    return null;
}

const OP_WORDS = { OPERATOR_ILIKE: "containing", OPERATOR_EQ: "=", OPERATOR_IN: "in", OPERATOR_NEQ: "!=", OPERATOR_GT: ">", OPERATOR_LT: "<", OPERATOR_GTE: ">=", OPERATOR_LTE: "<=" };

function describeQueryStep(step) {
    const entity = stepEntity(step) || "?";
    const parts = [];
    const walk = (node, depth) => {
        if (!node || typeof node !== "object" || depth > 24) return;
        if (Array.isArray(node)) { for (const c of node) walk(c, depth + 1); return; }
        if (node.pred) {
            const ref = refLabel(node.pred.value);
            if (ref) parts.push(`${String(node.pred.field?.field || "").replace(/^DIMENSION_FIELD_/, "").toLowerCase()} ${OP_WORDS[node.pred.op] || node.pred.op || ""} ${ref}`.trim());
            return;
        }
        for (const v of Object.values(node)) walk(v, depth + 1);
    };
    walk(step.spec?.root?.stepFilter, 0);
    const window = step.spec?.timeRange === "{{window}}" ? " in the window" : "";
    return `query ${entity}${parts.length ? ` where ${parts.join(" and ")}` : ""}${window}`;
}

export function describeRecipe(recipe) {
    const parts = (recipe.steps || []).map((s) => {
        if (s.kind === "search") return `search ${s.entity} for the term${s.window ? " in the window" : ""}`;
        if (s.kind === "query") return describeQueryStep(s);
        if (s.kind === "expand") {
            const max = s.anchors?.max ?? MAX_EXPAND_PER_RECIPE;
            const from = s.anchors?.from && s.anchors.from !== "search" ? `from step ${String(s.anchors.from).replace(/^s/, "")}` : "it found";
            return `expand up to ${max} ${s.anchors?.kind || "anchor"}${max === 1 ? "" : "s"} ${from}`;
        }
        return s.kind;
    });
    const text = parts.join(", then ");
    const expand = (recipe.steps || []).find((s) => s.kind === "expand");
    return !expand && recipe.yields?.kind ? `${text}, then return the ${recipe.yields.kind}s it found` : text;
}

// ── Running a plan ───────────────────────────────────────────────────

function idColumnsFor(kind) {
    return kind === "session" ? /(^|_)(trace_?id|session_?id)$/i : kind === "endpoint" ? /(^|_)endpoint_?id$/i : kind === "process" ? /(^|_)process_start_key$/i : null;
}

function anchorsFromRows(graph, rows, kind, { max = MAX_SEARCH_ANCHORS } = {}) {
    const counts = new Map();
    const idCols = idColumnsFor(kind);
    if (!idCols) return [];
    for (const row of rows || []) {
        for (const [col, value] of Object.entries(row || {})) {
            const tail = col.includes("__") ? col.slice(col.lastIndexOf("__") + 2) : col;
            if (!idCols.test(tail) || value == null || value === "") continue;
            const key = `${kind}:${value}`;
            if (!graph.nodes.has(key)) continue;
            counts.set(key, (counts.get(key) || 0) + 1);
        }
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, max).map(([key, n]) => ({ key, rows: n }));
}

function emitter(onUpdate, getDetails, intervalMs) {
    let timer = null;
    let dirty = false;
    const fire = () => { try { onUpdate?.({ details: getDetails() }); } catch { /* best effort */ } };
    return () => {
        if (!onUpdate) return;
        if (intervalMs <= 0) { fire(); return; }
        if (timer) { dirty = true; return; }
        fire();
        timer = setTimeout(() => { timer = null; if (dirty) { dirty = false; fire(); } }, intervalMs);
    };
}

function windowSpec(value) {
    let w = value;
    if (typeof w === "string") {
        const s = w.trim();
        const dur = /^(\d+)\s*([hdw])$/i.exec(s);
        if (dur) {
            const n = Number(dur[1]);
            const unit = dur[2].toLowerCase();
            const secs = unit === "h" ? n * 3600 : unit === "d" ? n * 86400 : n * 7 * 86400;
            return { relative: { durationSeconds: String(secs) } };
        }
        try { w = JSON.parse(s); } catch { w = null; }
    }
    if (w && typeof w === "object") {
        if (w.absolute) return { absolute: w.absolute };
        if (w.relative) return { relative: w.relative };
        if (w.start && w.end) return { absolute: { start: w.start, end: w.end } };
    }
    return { relative: { durationSeconds: String(SEARCH_DEFAULT_WINDOW_SECONDS) } };
}

function distinctValues(rows, column, limit = MAX_BOUND_VALUES) {
    const out = [];
    const seen = new Set();
    for (const row of rows || []) {
        const v = row?.[column];
        if (v == null || v === "") continue;
        const key = typeof v === "string" ? v : JSON.stringify(v);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(v);
        if (out.length >= limit) break;
    }
    return out;
}

// A query step's spec with its parameters and bindings filled. A pred whose
// value is exactly one binding takes the bound column's distinct values for
// an IN, the first value otherwise; inside a longer string the first value
// is substituted. The window parameter becomes a time range.
export function fillSpec(spec, { params = {}, question = null, rowsOf = () => [], missing = null } = {}) {
    const filled = JSON.parse(JSON.stringify(spec || {}));
    const bound = (ref) => {
        const m = /^s(\d+)\.([A-Za-z0-9_]+)$/.exec(ref);
        if (!m) return null;
        const vals = distinctValues(rowsOf(Number(m[1])), m[2]);
        if (!vals.length && Array.isArray(missing)) missing.push(ref);
        return vals;
    };
    const substitute = (text) => text.replace(ANY_REF, (_, ref) => {
        if (/^s\d+\./.test(ref)) { const vals = bound(ref); return vals && vals.length ? String(vals[0]) : ""; }
        const v = fillTemplate(`{{${ref}}}`, params, question);
        return v == null ? "" : (typeof v === "string" ? v : JSON.stringify(v));
    });
    const walk = (node, depth) => {
        if (!node || typeof node !== "object" || depth > 24) return;
        if (Array.isArray(node)) { for (const c of node) walk(c, depth + 1); return; }
        if (node.pred && typeof node.pred.value === "string") {
            const raw = node.pred.value.trim();
            const binding = WHOLE_BINDING.exec(raw);
            if (binding) {
                const vals = bound(`s${binding[1]}.${binding[2]}`) || [];
                node.pred.value = node.pred.op === "OPERATOR_IN" ? vals : (vals.length ? vals[0] : "");
            } else if (WHOLE_PARAM.test(raw)) {
                node.pred.value = fillTemplate(raw, params, question);
            } else {
                node.pred.value = substitute(node.pred.value);
            }
            return;
        }
        for (const v of Object.values(node)) walk(v, depth + 1);
    };
    walk(filled.root?.stepFilter, 0);
    if (filled.timeRange === "{{window}}") filled.timeRange = windowSpec(params.window);
    return filled;
}

// Run one recipe. `expandRun` is the expand tool's programmatic entry;
// `analyticsTool` the wrapped analytics_query (graph fed by its observer).
// `dryRun` runs the query steps and works out what the expand step would
// expand, without expanding: the replay gate uses it.
// What a filled spec asks for, in a line: the window and each filter, so
// a step that returns no rows can say what nothing matched (a window in
// the wrong year looks like any other empty result otherwise).
export function describeSpecFilters(spec) {
    const parts = [];
    const abs = spec?.timeRange?.absolute;
    if (abs?.start || abs?.end) parts.push(`window ${abs.start || "?"}..${abs.end || "?"}`);
    else if (spec?.timeRange?.relative) parts.push(`window ${JSON.stringify(spec.timeRange.relative)}`);
    const seen = new Set();
    const walk = (node, depth) => {
        if (!node || typeof node !== "object" || depth > 24) return;
        if (Array.isArray(node)) { for (const c of node) walk(c, depth + 1); return; }
        if (node.pred && node.pred.field) {
            const field = String(node.pred.field.field || node.pred.field.dimension || "").replace(/^DIMENSION_FIELD_/, "").toLowerCase();
            const op = String(node.pred.op || "").replace(/^OPERATOR_/, "").toLowerCase();
            const value = node.pred.value ?? (Array.isArray(node.pred.values) ? node.pred.values : null);
            const shown = Array.isArray(value) ? (value.length > 3 ? `${value.slice(0, 3).map((v) => JSON.stringify(v)).join(", ")} and ${value.length - 3} more` : value.map((v) => JSON.stringify(v)).join(", ")) : JSON.stringify(value);
            const line = `${field} ${op} ${shown}`;
            if (!seen.has(line)) { seen.add(line); parts.push(line); }
        }
        for (const v of Object.values(node)) walk(v, depth + 1);
    };
    walk(spec?.root, 0);
    return parts.join("; ");
}

// A multi-word term with any one character allowed between its words (the
// ILIKE `_`), so the spelling the traces use ("agent-test", "agent_test",
// "agent tests") matches the one the question used. Not `%`: with anything
// allowed between the words, three common words match most summaries, and
// the plan expands noise. Null for one word, a term that already carries
// wildcards, or a term the gap would leave as it is.
export function gapTerm(term) {
    if (typeof term !== "string" || term.includes("%")) return null;
    const given = term.trim();
    const words = given.split(/[\s_-]+/).filter(Boolean);
    const loose = words.join("_");
    return words.length > 1 && loose !== given ? loose : null;
}

// The step's spec with each multi-word term it takes loosened, or null
// when it takes none. Only textual parameters are touched (the distiller
// names them term, term2, ...): a value parameter is an id or a number,
// and a wildcard inside one matches nothing.
function loosenedSpec(step, { spec, params, question, schema, rowsOf }) {
    if (step.kind === "search") {
        const given = fillTemplate(step.term, params, question);
        const loose = gapTerm(given);
        if (!loose) return null;
        const built = searchSpec({ schema, entity: step.entity, term: `%${loose}%`, window: fillTemplate(step.window, params, question), limit: step.limit });
        return { given: JSON.stringify(given), loose: JSON.stringify(loose), spec: built.spec };
    }
    const template = JSON.stringify(step.spec || {});
    const names = Object.keys(params || {}).filter((name) => /^term\d*$/.test(name) && gapTerm(params[name]) && template.includes(`{{${name}}}`));
    if (!names.length) return null;
    const looser = Object.fromEntries(names.map((name) => [name, gapTerm(params[name])]));
    const filled = fillSpec(step.spec, { params: { ...params, ...looser }, question, rowsOf, missing: [] });
    if (JSON.stringify(filled) === JSON.stringify(spec)) return null;
    return { given: names.map((n) => JSON.stringify(params[n])).join(", "), loose: names.map((n) => JSON.stringify(looser[n])).join(", "), spec: filled };
}

export async function runRecipe({ recipe, params = {}, question = null, graph, schema, analyticsTool, expandRun, getRows = null, toolCallId = "recipe", signal = null, onUpdate = null, emitIntervalMs = 150, dryRun = false }) {
    const state = { kind: "recipe", id: recipe.id, name: recipe.name, source: recipe.source || "distilled", phase: "running", params: { ...params }, question: question || null, steps: [], claims: [], error: null };
    const emit = emitter(onUpdate, () => JSON.parse(JSON.stringify(state)), emitIntervalMs);
    emit();
    const before = new Set(graph.nodes.keys());
    // Rows per query step, by 1-based step number, for bindings and anchors;
    // kept out of the emitted state.
    const rowsByStep = new Map();
    const rowsOf = (n) => rowsByStep.get(n) || [];
    const wantKind = (recipe.steps || []).find((s) => s.kind === "expand")?.anchors?.kind || recipe.yields?.kind || "session";
    if (recipe.yields) state.yields = recipe.yields;
    try {
        for (const [i, step] of (recipe.steps || []).entries()) {
            if (signal?.aborted) throw new Error("aborted");
            const n = i + 1;
            if (step.kind === "search" || step.kind === "query") {
                const record = { kind: step.kind, entity: stepEntity(step), field: step.kind === "search" ? termFieldFor(step.entity) : null, term: step.kind === "search" ? fillTemplate(step.term, params, question) : null, rows: 0, toolCallId: `${toolCallId}:s${n}`, anchors: [], notes: [], error: null };
                state.steps.push(record);
                emit();
                try {
                    let spec;
                    if (step.kind === "search") {
                        const built = searchSpec({ schema, entity: step.entity, term: record.term, window: fillTemplate(step.window, params, question), limit: step.limit });
                        record.notes = built.notes;
                        spec = built.spec;
                    } else {
                        const missing = [];
                        spec = fillSpec(step.spec, { params, question, rowsOf, missing });
                        // A binding with nothing behind it (the step it points
                        // at failed or returned no rows) would go out as an
                        // empty IN list and fail validation; say so instead.
                        if (missing.length) throw new Error(`bound values missing: ${[...new Set(missing)].join(", ")} (that step returned no rows)`);
                    }
                    record.filters = describeSpecFilters(spec);
                    // The spec whose rows are stored under the step's id; the
                    // kernel extractor lifts it from the run's details.
                    record.spec = spec;
                    let out = await analyticsTool.execute(record.toolCallId, { spec }, signal);
                    let stored = getRows?.(record.toolCallId);
                    let preview = rowsFromContent(out);
                    let rows = Array.isArray(stored) ? stored : preview.rows;
                    // The term is the model's guess at how the traces spell the
                    // name in the question. When a multi-word term finds
                    // nothing, retry once with any one character allowed
                    // between its words, so "agent test" also matches
                    // "agent-test", "agent_test" and "agent tests". The step
                    // says so, and the looser spec is what any widening uses.
                    if (!rows.length) {
                        const loosened = loosenedSpec(step, { spec, params, question, schema, rowsOf });
                        if (loosened) {
                            out = await analyticsTool.execute(record.toolCallId, { spec: loosened.spec }, signal);
                            stored = getRows?.(record.toolCallId);
                            preview = rowsFromContent(out);
                            rows = Array.isArray(stored) ? stored : preview.rows;
                            record.loosened = { from: loosened.given, to: loosened.loose };
                            spec = loosened.spec;
                            record.spec = spec;
                            if (rows.length) record.filters = describeSpecFilters(spec);
                        }
                    }
                    // The window is the model's guess at when something happened.
                    // When the plan's first step finds nothing inside it, widen
                    // once by the window's own span on each side before giving
                    // up: a guess that was thirty minutes off should not fail
                    // the plan. A window the model got wrong by a year still
                    // fails, and the step says what it searched.
                    if (!rows.length && n === 1 && step.kind === "query" && step.spec?.timeRange === "{{window}}" && spec?.timeRange?.absolute?.start && spec?.timeRange?.absolute?.end) {
                        const start = Date.parse(spec.timeRange.absolute.start);
                        const end = Date.parse(spec.timeRange.absolute.end);
                        if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
                            const span = end - start;
                            const wider = { ...spec, timeRange: { absolute: { start: new Date(start - span).toISOString(), end: new Date(end + span).toISOString() } } };
                            out = await analyticsTool.execute(record.toolCallId, { spec: wider }, signal);
                            stored = getRows?.(record.toolCallId);
                            preview = rowsFromContent(out);
                            rows = Array.isArray(stored) ? stored : preview.rows;
                            record.spec = wider;
                            record.widened = `${wider.timeRange.absolute.start}..${wider.timeRange.absolute.end}`;
                            if (rows.length) record.filters = describeSpecFilters(wider);
                        }
                    }
                    rowsByStep.set(n, rows);
                    record.rows = Array.isArray(stored) ? stored.length : preview.total;
                    record.anchors = anchorsFromRows(graph, rows, wantKind);
                } catch (err) {
                    record.error = err?.message || String(err);
                }
                emit();
            } else if (step.kind === "expand") {
                const fromRef = step.anchors?.from;
                const fromN = typeof fromRef === "string" && /^s\d+$/.test(fromRef) ? Number(fromRef.slice(1)) : null;
                const queries = state.steps.filter((s) => s.kind === "search" || s.kind === "query");
                const from = fromN ? state.steps[fromN - 1] : ([...queries].reverse().find((s) => s.anchors?.length) || queries[0]);
                const max = Math.max(1, Math.min(MAX_EXPAND_PER_RECIPE, Number(step.anchors?.max) || MAX_EXPAND_PER_RECIPE));
                const kind = step.anchors?.kind || "session";
                // The rows of the step the plan converges on go into the
                // result: the model answered from these rows when it wrote
                // the plan, and without them it re-ran the queries itself.
                if (from && !from.excerpt) {
                    const fromRows = rowsOf(state.steps.indexOf(from) + 1);
                    if (fromRows.length) {
                        try { from.excerpt = rowExcerpt([{ to: from.entity, objects: fromRows, total: from.rows || fromRows.length }], { rows: 6, bytes: 1400, term: fillTemplate(step.term, params, question), question }); } catch { /* the excerpt is a courtesy */ }
                    }
                }
                // The anchors to expand are the ones the named step, the
                // narrowest query of the plan, returned: ranked by their rows
                // in that step, ties broken by how many other steps carried
                // them. The pool of every step's anchors ranked by volume put
                // the noisiest session first (the one with 72 rows of
                // unrelated commands over the one with 7 rows of the asked
                // command); it only stands in when the named step has none.
                const pool = new Map();
                for (const q of queries) {
                    for (const a of q.anchors || []) {
                        if (!a.key.startsWith(`${kind}:`)) continue;
                        const e = pool.get(a.key) || { key: a.key, steps: 0, rows: 0, named: false, namedRows: 0 };
                        e.steps++;
                        e.rows += a.rows || 0;
                        if (q === from) { e.named = true; e.namedRows = a.rows || 0; }
                        pool.set(a.key, e);
                    }
                }
                const named = [...pool.values()].filter((e) => e.named).sort((a, b) => (b.namedRows - a.namedRows) || (b.steps - a.steps) || (b.rows - a.rows));
                const ranked = named.length ? named : [...pool.values()].sort((a, b) => (b.steps - a.steps) || (b.rows - a.rows));
                const targets = ranked.slice(0, max);
                const record = { kind: "expand", anchorKind: kind, from: from?.toolCallId || null, candidates: targets.map((a) => a.key), ranked: ranked.slice(0, 8).map((a) => ({ key: a.key, rows: a.named ? a.namedRows : a.rows, steps: a.steps })), results: targets.map((a) => ({ kind: "expand", anchor: a.key, phase: "pending", steps: [], skipped: [], reader: null })), error: targets.length ? null : (from?.error ? "the step it expands from failed" : "the plan produced no anchors to expand") };
                state.steps.push(record);
                emit();
                if (dryRun) continue;
                for (const [j, target] of targets.entries()) {
                    if (signal?.aborted) throw new Error("aborted");
                    const res = await expandRun(`${toolCallId}:e${n}.${j + 1}`, target.key, { term: fillTemplate(step.term, params, question), question: fillTemplate(step.question, params, question) || question }, signal, (partial) => { if (partial?.details) record.results[j] = partial.details; emit(); });
                    record.results[j] = res;
                    for (const c of res?.reader?.claims || []) if (c && c.statement) state.claims.push({ ...c, anchor: target.key });
                    emit();
                }
            }
        }
        state.phase = "done";
    } catch (err) {
        state.phase = "error";
        state.error = err?.message || String(err);
    }
    state.newKeys = [...graph.nodes.keys()].filter((k) => !before.has(k));
    emit();
    return state;
}

// What the tool row says while a recipe runs and when it is done.
export function recipeProgressLabel(details) {
    if (!details || details.kind !== "recipe") return null;
    if (details.error && details.phase === "error") return `error: ${String(details.error).slice(0, 80)}`;
    const steps = Array.isArray(details.steps) ? details.steps : [];
    const queries = steps.filter((s) => s.kind === "search" || s.kind === "query");
    const last = queries[queries.length - 1];
    const expand = steps.find((s) => s.kind === "expand");
    if (details.phase === "running") {
        if (!last) return "starting…";
        if (!expand) return last.error ? `${last.kind} failed` : (last.rows ? `${last.rows} rows${queries.length > 1 ? ` (step ${queries.length})` : ""}, expanding…` : `${last.kind === "search" ? "searching" : "querying"} ${last.entity}…`);
        const done = (expand.results || []).filter((r) => r?.phase === "done" || r?.phase === "error").length;
        return `expanding ${done}/${(expand.results || []).length}…`;
    }
    const claims = Array.isArray(details.claims) ? details.claims : [];
    const supported = claims.filter((c) => c.status === "supported").length;
    const rows = queries.reduce((n, s) => n + (s.rows || 0), 0);
    const anchors = expand?.candidates?.length ?? last?.anchors?.length ?? 0;
    return `${rows} row${rows === 1 ? "" : "s"}${queries.length > 1 ? ` over ${queries.length} steps` : ""}, ${anchors} anchor${anchors === 1 ? "" : "s"}, ${claims.length} claim${claims.length === 1 ? "" : "s"}${claims.length ? ` (${supported} supported)` : ""}`;
}

// The question this run answered: the last user message with text.
export function lastUserText(messages) {
    for (let i = (messages || []).length - 1; i >= 0; i--) {
        const m = messages[i];
        if (!m || m.role !== "user") continue;
        if (typeof m.content === "string" && m.content.trim()) return m.content.trim();
        if (Array.isArray(m.content)) {
            const text = m.content.filter((b) => b && b.type === "text" && typeof b.text === "string").map((b) => b.text).join("\n").trim();
            if (text) return text;
        }
    }
    return null;
}

export function recipeSummary(state) {
    const lines = [`recipe ${state.name}${state.phase === "error" ? ` failed: ${state.error}` : ""}`];
    for (const s of state.steps || []) {
        if (s.kind === "search" || s.kind === "query") {
            const head = s.kind === "search" ? `search ${s.entity} for "${s.term}"` : `query ${s.entity}`;
            lines.push(`  - ${head}: ${s.error ? `error: ${String(s.error).split("\n")[0].slice(0, 160)}` : `${s.rows} row${s.rows === 1 ? "" : "s"} (tool_call_id ${s.toolCallId})`}${s.anchors?.length ? `; ${s.anchors.length} ${s.anchors.length === 1 ? "anchor" : "anchors"} found: ${s.anchors.map((a) => `${a.key} (${a.rows} rows)`).join(", ")}` : ""}${s.notes?.length ? `; ${s.notes.join("; ")}` : ""}`);
            if (s.loosened) lines.push(`      ${s.rows ? `nothing for ${s.loosened.from}; found as ${s.loosened.to}` : `nothing for ${s.loosened.from}, nor as ${s.loosened.to}`} (any one character between its words)`);
            if (s.widened) lines.push(`      ${s.rows ? "nothing in the given window; found after widening it to" : "nothing in the given window, nor after widening it to"} ${s.widened}`);
            if (!s.error && !s.rows && s.filters) lines.push(`      nothing matched ${s.filters}`);
            if (s.excerpt) for (const l of String(s.excerpt).split("\n")) lines.push(`      ${l}`);
        } else if (s.kind === "expand") {
            if (s.error) { lines.push(`  - expand: ${s.error}`); continue; }
            const leftOut = (s.ranked || []).filter((r) => !(s.candidates || []).includes(r.key));
            if (leftOut.length) lines.push(`  - not expanded: ${leftOut.map((r) => `${r.key} (${r.rows} row${r.rows === 1 ? "" : "s"})`).join(", ")}`);
            for (const r of s.results || []) {
                const claims = r?.reader?.claims || [];
                lines.push(`  - expand ${r.anchor}: ${(r.steps || []).length} pivots, ${r.reader ? `${claims.length} claim${claims.length === 1 ? "" : "s"}` : "no reader"}`);
                for (const l of (Array.isArray(r.slice) ? r.slice : []).slice(0, 5)) lines.push(`      ${l}`);
                for (const c of claims) lines.push(`      ${c.id || "-"} ${c.kind || ""} ${c.status}${c.bearing === "context" ? " [context]" : ""}: ${c.statement || c.reason || ""}`);
            }
        }
    }
    if (!(state.steps || []).some((s) => s.kind === "expand")) {
        const fromN = Number(String(state.yields?.from || "").replace(/^s/, "")) || 0;
        const named = fromN > 0 ? (state.steps || [])[fromN - 1] : null;
        const last = (named?.anchors?.length ? named : null) || [...(state.steps || [])].reverse().find((s) => (s.kind === "search" || s.kind === "query") && s.anchors?.length);
        if (last) lines.push(`  - anchors to read or expand: ${last.anchors.slice(0, 8).map((a) => `${a.key} (${a.rows} row${a.rows === 1 ? "" : "s"})`).join(", ")}`);
    }
    if (state.newKeys?.length) lines.push(`graph: +${state.newKeys.length} anchor${state.newKeys.length === 1 ? "" : "s"}. Rows are stored under the tool_call_ids above.`);
    return lines.join("\n");
}

// ── The run trace ────────────────────────────────────────────────────

// What a tool call becomes in the run trace the distiller reads: the tool,
// whether it succeeded, the shape of what it asked for, and for a query the
// spec itself so the plan can be rebuilt from it. A tool that builds the
// query for the model (pivot) reports the spec it ran as details.querySpec.
export function traceEntry(tool, toolCallId, args, ok, out = null) {
    const given = args?.spec && typeof args.spec === "object" ? args.spec : null;
    const ran = out?.details?.querySpec && typeof out.details.querySpec === "object" ? out.details.querySpec : null;
    const spec = given || ran;
    const preds = [];
    const walk = (node, depth) => {
        if (!node || typeof node !== "object" || depth > 24) return;
        if (Array.isArray(node)) { for (const c of node) walk(c, depth + 1); return; }
        if (node.pred) preds.push({ field: String(node.pred.field?.field || "").replace(/^DIMENSION_FIELD_/, ""), op: node.pred.op, value: typeof node.pred.value === "string" ? node.pred.value.slice(0, 120) : node.pred.value });
        for (const v of Object.values(node)) walk(v, depth + 1);
    };
    if (spec) walk(spec.root?.stepFilter, 0);
    let keptSpec = null;
    if (spec) {
        try { const text = JSON.stringify(spec); if (text.length <= MAX_TRACED_SPEC_CHARS) keptSpec = JSON.parse(text); } catch { keptSpec = null; }
    }
    return {
        name: tool,
        toolCallId,
        ok: Boolean(ok),
        entity: spec ? String(spec.root?.entity || "").replace(/^ENTITY_TYPE_/, "") : (args?.to ? String(args.to) : null),
        preds,
        spec: keptSpec,
        anchor: typeof args?.anchor === "string" ? args.anchor : null,
        anchors: Array.isArray(args?.anchors) ? args.anchors.filter((a) => typeof a === "string") : null,
        term: typeof args?.term === "string" ? args.term : null,
        claimKind: tool === "claim" ? args?.kind || null : null,
        recipe: tool === "run_recipe" ? args?.name || null : null,
    };
}

// A trace entry the plan can be built from: the model's own query, or a
// pivot, which ran a query the model chose without writing its spec.
function isQueryEntry(t) {
    return t?.name === "analytics_query" || (t?.name === "pivot" && Boolean(t.spec));
}

// A tool that returns instead of throwing can still report a failure: a
// recipe that did not run, or an error result. Those calls are in the
// trace but not ok, so a failed run_recipe does not count as having
// followed a recipe.
function resultReportsError(out) {
    if (!out || typeof out !== "object") return false;
    if (out.isError === true) return true;
    if (out.details && typeof out.details === "object" && out.details.phase === "error") return true;
    return false;
}

export function withTrace(tool, trace) {
    if (!tool || typeof tool.execute !== "function" || !Array.isArray(trace)) return tool;
    return {
        ...tool,
        async execute(toolCallId, args, ...rest) {
            try {
                const out = await tool.execute(toolCallId, args, ...rest);
                trace.push(traceEntry(tool.name, toolCallId, args, !resultReportsError(out), out));
                return out;
            } catch (err) {
                trace.push(traceEntry(tool.name, toolCallId, args, false));
                throw err;
            }
        },
    };
}

function hash(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
}

// ── Distillation ─────────────────────────────────────────────────────

// Every cell of a step's rows, as lowercase text, mapped to the column it
// came from. This is how the distiller finds where a later literal came
// from.
function indexRows(rows) {
    const index = new Map();
    let cells = 0;
    for (const row of (rows || []).slice(0, MAX_INDEXED_ROWS)) {
        for (const [col, v] of Object.entries(row || {})) {
            if (v == null || v === "") continue;
            const text = (typeof v === "string" ? v : JSON.stringify(v)).toLowerCase();
            if (text.length < 3) continue;
            if (!index.has(text)) index.set(text, col);
            if (++cells >= MAX_INDEXED_CELLS) return index;
        }
    }
    return index;
}

function literalText(v) {
    if (v == null) return null;
    return typeof v === "string" ? v : typeof v === "number" || typeof v === "boolean" ? String(v) : null;
}

function stripLike(s) {
    return String(s).replace(/^%+|%+$/g, "").trim();
}

// The literals a spec's preds carry, as lowercase text, for the relevance
// walk (what did this step consume?).
function predLiterals(spec) {
    const out = [];
    const walk = (node, depth) => {
        if (!node || typeof node !== "object" || depth > 24) return;
        if (Array.isArray(node)) { for (const c of node) walk(c, depth + 1); return; }
        if (node.pred) {
            const v = node.pred.value;
            const vals = Array.isArray(v) ? v : [v];
            for (const x of vals) { const t = literalText(x); if (t != null) out.push(stripLike(t).toLowerCase()); }
            return;
        }
        for (const v of Object.values(node)) walk(v, depth + 1);
    };
    walk(spec?.root?.stepFilter, 0);
    return out.filter((t) => t.length >= 3);
}

// The steps a run's own queries make once dead ends are dropped: walking
// backwards from what the answer was built on (the anchors expanded, the
// nodes the claims cite), a query stays when its rows produced a value a
// kept later step consumed; a kept step's own literals become what earlier
// steps must have produced.
// The plan is the chain of queries that fed each other on the way to the
// answer, walked backward from what the answer cited: a step is kept when
// its rows provide a value a later kept step needed, and once a value has
// a provider it is no longer looked for, so the exploratory queries that
// merely touched the same ids do not come along.
export const MAX_PLAN_QUERIES = 4;
function relevantQueries(queries, sinks) {
    const needed = new Set(sinks.map((s) => String(s).toLowerCase()));
    const kept = [];
    for (let i = queries.length - 1; i >= 0; i--) {
        const q = queries[i];
        const provided = [...q.index.keys()].filter((text) => needed.has(text));
        if (!provided.length) continue;
        kept.unshift(q);
        for (const text of provided) needed.delete(text);
        for (const lit of predLiterals(q.spec)) needed.add(lit);
    }
    return kept;
}

// Turn a kept query's spec into a template. A literal that appears in the
// question becomes a parameter; a literal that appears in an earlier kept
// step's rows becomes a binding to that step and column; an absolute time
// range becomes the window parameter; anything else stays literal.
function templateSpec(spec, { question, earlier, params, example }) {
    const out = JSON.parse(JSON.stringify(spec));
    const q = String(question || "").toLowerCase();
    const inQuestion = (t) => t.length >= 3 && q.includes(t.toLowerCase());
    const findEarlier = (t) => {
        const key = String(t).toLowerCase();
        for (const e of earlier) { const col = e.index.get(key); if (col) return { step: e.n, col }; }
        return null;
    };
    const paramFor = (value, textual) => {
        const existing = params.find((p) => p.name !== "window" && String(example.params[p.name]).toLowerCase() === String(value).toLowerCase());
        if (existing) return existing.name;
        const base = textual ? "term" : "value";
        const count = params.filter((p) => p.name.startsWith(base)).length;
        const name = count ? `${base}${count + 1}` : base;
        params.push({ name, required: true, description: `${textual ? "text to look for" : "a value"}, from the question; here it was "${String(value).slice(0, 60)}"` });
        example.params[name] = value;
        return name;
    };
    const walk = (node, depth) => {
        if (!node || typeof node !== "object" || depth > 24) return;
        if (Array.isArray(node)) { for (const c of node) walk(c, depth + 1); return; }
        if (node.pred) {
            const v = node.pred.value;
            if (Array.isArray(v)) {
                // An IN list the model assembled from earlier rows binds to the
                // step and column that hold most of its values; ids the model
                // gathered from several steps, or typed in, do not belong in a
                // plan, so a list with no home stays literal only when none of
                // it came from earlier rows.
                const texts = v.map(literalText).filter((t) => t != null);
                if (texts.length && texts.length === v.length) {
                    const votes = new Map();
                    for (const t of texts) { const h = findEarlier(t); if (h) { const k = `s${h.step}.${h.col}`; votes.set(k, (votes.get(k) || 0) + 1); } }
                    const [home] = [...votes.entries()].sort((a, b) => b[1] - a[1])[0] || [];
                    if (home) node.pred.value = `{{${home}}}`;
                }
                return;
            }
            const text = literalText(v);
            if (text == null) return;
            if (node.pred.op === "OPERATOR_ILIKE") {
                const core = stripLike(text);
                const wrapped = (inner) => (text.startsWith("%") || text.endsWith("%") ? `%${inner}%` : inner);
                if (inQuestion(core)) { node.pred.value = wrapped(`{{${paramFor(core, true)}}}`); return; }
                const home = findEarlier(core);
                if (home) { node.pred.value = wrapped(`{{s${home.step}.${home.col}}}`); return; }
                // A refinement the model wrote around a value from the question
                // ("action=unlock" for the unlock question) keeps its shape and
                // takes the parameter inside: "action={{term}}".
                let partial = core;
                for (const p of params) {
                    const v = example.params[p.name];
                    if (typeof v !== "string" || v.length < 4) continue;
                    const at = partial.toLowerCase().indexOf(v.toLowerCase());
                    if (at < 0) continue;
                    partial = `${partial.slice(0, at)}{{${p.name}}}${partial.slice(at + v.length)}`;
                }
                if (partial !== core) node.pred.value = wrapped(partial);
                return;
            }
            const home = findEarlier(text);
            if (home) {
                // The model picked one value out of the earlier step's rows;
                // the plan takes all of them, so the bound pred becomes IN.
                node.pred.value = `{{s${home.step}.${home.col}}}`;
                if (node.pred.op === "OPERATOR_EQ") node.pred.op = "OPERATOR_IN";
                return;
            }
            if (inQuestion(text)) node.pred.value = `{{${paramFor(text, false)}}}`;
            return;
        }
        for (const val of Object.values(node)) walk(val, depth + 1);
    };
    walk(out.root?.stepFilter, 0);
    if (out.timeRange && typeof out.timeRange === "object" && out.timeRange.absolute) {
        // Steps may have used different windows; the plan takes the first
        // kept step's, the moment the question is about. Taking the union
        // ran a fleet-wide text search over a lookup step's ten-day window
        // and hit the execution limit; a lookup bound to ids from earlier
        // steps loses nothing by running over the moment.
        if (!example.params.window) example.params.window = out.timeRange.absolute;
        out.timeRange = "{{window}}";
        if (!params.some((p) => p.name === "window")) params.push({ name: "window", required: false, description: "{ start, end } in RFC 3339 UTC around the moment; default the last 24 hours" });
    }
    return out;
}

function entityLabel(entity) {
    return String(entity || "").toLowerCase().replace(/_/g, " ");
}

// The trigger and name when no model wrote them: the shape of the plan,
// never the question.
export function fallbackTrigger(recipe) {
    const queries = (recipe.steps || []).filter((s) => s.kind === "search" || s.kind === "query");
    const expand = (recipe.steps || []).find((s) => s.kind === "expand");
    const first = entityLabel(stepEntity(queries[0]));
    const hops = queries.slice(1).map((s) => entityLabel(stepEntity(s)));
    const kind = expand?.anchors?.kind || recipe.yields?.kind || "session";
    const when = `a question that gives text or a value to find in ${first}${hops.length ? `, followed through ${hops.join(" and ")}` : ""}, asking about the ${kind}s involved`;
    const name = `${first} search${hops.length ? ` via ${hops.join(", ")}` : ""}, then ${expand ? "expand" : "return"} ${kind}s`;
    return { when, name };
}

// A run becomes a recipe in waiting when its own queries, pruned to the
// ones whose rows fed what the answer was built on, take at least one
// parameter from the question and lead to an expand that ended in a
// supported attribution or activity claim. Returns the candidate and the
// example that reproduces it, or null. Nothing is stored here: the replay
// gate and the describer come after.
// How many tool calls the model made in the turn that just ended: every
// assistant tool call after the last user message. The trace covers only
// the graph tools, so a run that saved two row-paging calls looked no
// cheaper by the trace; the savings a recipe reports are in these calls.
export function countRunToolCalls(messages) {
    let n = 0;
    for (let i = (messages || []).length - 1; i >= 0; i--) {
        const m = messages[i];
        if (!m) continue;
        if (m.role === "user") {
            const hasText = typeof m.content === "string" ? m.content.trim().length > 0 : Array.isArray(m.content) && m.content.some((b) => b && b.type === "text" && String(b.text || "").trim());
            if (hasText) break;
            continue;
        }
        if (m.role !== "assistant" || !Array.isArray(m.content)) continue;
        n += m.content.filter((b) => b && (b.type === "toolCall" || b.type === "tool_use")).length;
    }
    return n;
}

export function distillRecipeFromRun({ trace, question, graph, getRows = null, calls = null }) {
    if (!Array.isArray(trace) || !trace.length) return null;
    if (trace.some((t) => t.name === "run_recipe" && t.ok)) return null; // it already followed one
    const topLevel = (t) => !String(t.toolCallId || "").includes(":");
    const runIds = new Set(trace.map((t) => t.toolCallId));
    const fromThisRun = (c) => c?.source && (runIds.has(c.source.toolCallId) || [...runIds].some((id) => String(c.source.toolCallId || "").startsWith(`${id}`)));
    const claims = (graph?.claims || []).filter((c) => c.status === "supported" && (c.kind === "attribution" || c.kind === "activity") && fromThisRun(c));
    if (!claims.length) return null;
    const isExpand = (t) => t.ok && topLevel(t) && (t.name === "expand" || t.name === "expand_all");
    const expands = trace.filter(isExpand);
    const expandedKeys = expands.flatMap((t) => (t.anchors || (t.anchor ? [t.anchor] : [])));
    // A run that reached its claims with queries alone (no expand) yields a
    // plan that ends in rows: the anchors it converges on are the graph
    // nodes the supported claims cited, and the model reads or expands them.
    const queryOnly = !expandedKeys.length;
    const anchorKeys = queryOnly
        ? [...new Set(claims.flatMap((c) => (Array.isArray(c.evidence) ? c.evidence : [])).filter((k) => typeof k === "string" && k.includes(":") && graph?.nodes?.has(k)))]
        : expandedKeys;
    if (!anchorKeys.length) return null;
    const kinds = new Map();
    for (const k of anchorKeys) { const kind = k.slice(0, k.indexOf(":")); if (kind) kinds.set(kind, (kinds.get(kind) || 0) + 1); }
    const [kind] = [...kinds.entries()].sort((a, b) => b[1] - a[1])[0];
    // The plan's queries: the model's own before it expanded, or before its
    // first claim when it never expanded, with a spec the trace kept and
    // rows to reason from.
    const firstClaimIdx = trace.findIndex((t) => t.ok && topLevel(t) && t.name === "claim");
    const cutoff = queryOnly ? (firstClaimIdx >= 0 ? firstClaimIdx : trace.length) : trace.findIndex(isExpand);
    const queries = [];
    trace.forEach((t, i) => {
        if (i > cutoff || !t.ok || !topLevel(t) || !isQueryEntry(t) || !t.spec || !t.entity) return;
        const rows = getRows?.(t.toolCallId) || [];
        if (!Array.isArray(rows) || !rows.length) return;
        queries.push({ entry: t, spec: t.spec, rows, index: indexRows(rows) });
    });
    if (!queries.length) return null;
    const idOf = (k) => String(k).slice(String(k).indexOf(":") + 1);
    const sinks = [
        ...anchorKeys.map(idOf),
        ...claims.flatMap((c) => (Array.isArray(c.evidence) ? c.evidence : []).map(idOf)),
    ];
    const kept = relevantQueries(queries, sinks);
    if (!kept.length || kept.length > MAX_PLAN_QUERIES) return null; // nothing led to the answer, or too much did to be a plan
    kept.forEach((q, i) => { q.n = i + 1; });
    const params = [];
    const example = { question: String(question || ""), params: {} };
    const steps = kept.map((q) => ({ kind: "query", spec: templateSpec(q.spec, { question, earlier: kept.filter((e) => e.n < q.n), params, example }) }));
    if (!params.some((p) => p.name !== "window")) return null; // a plan with nothing from the question is a replay, not a recipe
    // The step the expand takes its anchors from: the kept step where the
    // expanded anchors rank best among that step's own anchors (a narrow
    // follow-up ranks the session first; a 200-row search buries it), later
    // steps winning ties.
    const expectedKeys = new Set(anchorKeys);
    let fromStep = kept[kept.length - 1];
    let bestRank = Infinity;
    for (const q of kept) {
        const ranked = anchorsFromRows(graph, q.rows, kind, { max: MAX_SEARCH_ANCHORS });
        const rank = ranked.findIndex((a) => expectedKeys.has(a.key));
        if (rank >= 0 && rank <= bestRank) { bestRank = rank; fromStep = q; }
    }
    if (!queryOnly) {
        const expandTerm = expands.map((t) => t.term).find(Boolean) || null;
        const termParam = expandTerm ? params.find((p) => p.name !== "window" && String(example.params[p.name]).toLowerCase() === expandTerm.toLowerCase()) : null;
        steps.push({ kind: "expand", anchors: { kind, from: `s${fromStep.n}`, max: MAX_EXPAND_PER_RECIPE }, term: termParam ? `{{${termParam.name}}}` : expandTerm, question: "{{question}}" });
    }
    // What this run cost: the root's own tool calls. A recipe earns its keep
    // only when a run that follows it comes in under this.
    const rootCalls = Number.isFinite(calls) ? calls : trace.filter(topLevel).length;
    example.calls = rootCalls;
    const draft = { id: `r_${hash(`${kept.map((q) => q.entry.entity).join(">")}|${queryOnly ? "rows:" : ""}${kind}|${Date.now()}`)}`, source: "distilled", version: RECIPE_VERSION, params, steps, example, examples: [example], origin: { calls: rootCalls }, ...(queryOnly ? { yields: { kind, from: `s${fromStep.n}` } } : {}) };
    const { when, name } = fallbackTrigger(draft);
    return { recipe: { ...draft, name, when }, example, expected: { anchors: anchorKeys } };
}

// ── Generalizing across runs ─────────────────────────────────────────

function collectPreds(spec) {
    const out = [];
    const walk = (node, depth) => {
        if (!node || typeof node !== "object" || depth > 24) return;
        if (Array.isArray(node)) { for (const c of node) walk(c, depth + 1); return; }
        if (node.pred) { out.push(node.pred); return; }
        for (const v of Object.values(node)) walk(v, depth + 1);
    };
    walk(spec?.root?.stepFilter, 0);
    return out;
}

const HAS_REF = /\{\{[^}]+\}\}/;
function paramNamesIn(value) {
    const names = [];
    for (const m of String(value).matchAll(/\{\{(\w+)\}\}/g)) names.push(m[1]);
    return names;
}

// Two runs that took the same path are one recipe, and the second run can
// teach the first something: where the stored plan kept a literal that the
// new run took from the question or from an earlier step, the stored plan
// takes the parameter or binding, and the new question joins the recipe's
// examples. The origin cost is the lower of the two: the bar a run following
// the recipe has to beat.
export function mergeRecipeTemplates(existing, incoming) {
    const merged = JSON.parse(JSON.stringify(existing));
    merged.params = Array.isArray(merged.params) ? merged.params : [];
    let generalized = false;
    const inSteps = Array.isArray(incoming?.steps) ? incoming.steps : [];
    (merged.steps || []).forEach((a, i) => {
        const b = inSteps[i];
        if (!b || a.kind !== "query" || b.kind !== "query") return;
        const pa = collectPreds(a.spec);
        const pb = collectPreds(b.spec);
        for (let j = 0; j < Math.min(pa.length, pb.length); j++) {
            const sameField = String(pa[j].field?.field || "") === String(pb[j].field?.field || "");
            const aRef = typeof pa[j].value === "string" && HAS_REF.test(pa[j].value);
            const bRef = typeof pb[j].value === "string" && HAS_REF.test(pb[j].value);
            if (!sameField || aRef || !bRef) continue;
            pa[j].value = pb[j].value;
            if (pb[j].op) pa[j].op = pb[j].op;
            generalized = true;
            for (const name of paramNamesIn(pb[j].value)) {
                if (merged.params.some((p) => p.name === name)) continue;
                const p = (incoming.params || []).find((x) => x.name === name);
                if (p) merged.params.push({ ...p });
            }
        }
        if (a.spec && a.spec.timeRange !== "{{window}}" && b.spec?.timeRange === "{{window}}") {
            a.spec.timeRange = "{{window}}";
            generalized = true;
            if (!merged.params.some((p) => p.name === "window")) {
                const p = (incoming.params || []).find((x) => x.name === "window");
                if (p) merged.params.push({ ...p });
            }
        }
    });
    const examples = Array.isArray(merged.examples) ? merged.examples : (merged.example ? [merged.example] : []);
    let newExample = false;
    for (const e of (Array.isArray(incoming?.examples) ? incoming.examples : (incoming?.example ? [incoming.example] : []))) {
        if (!e?.question || examples.some((x) => x.question === e.question)) continue;
        examples.push(e);
        newExample = true;
    }
    merged.examples = examples.slice(-MAX_EXAMPLES);
    if (!merged.example && merged.examples.length) merged.example = merged.examples[0];
    const incomingCalls = incoming?.origin?.calls;
    if (Number.isFinite(incomingCalls)) merged.origin = { calls: Number.isFinite(merged.origin?.calls) ? Math.min(merged.origin.calls, incomingCalls) : incomingCalls };
    return { recipe: merged, generalized, newExample };
}

// Why a run produced no candidate: the first of the distiller's
// preconditions it fails, in the distiller's order, as one sentence the
// person can act on. Only called when distillRecipeFromRun returned null.
export function whyNoRecipe({ trace, graph, getRows = null }) {
    if (!Array.isArray(trace) || !trace.length) return "the run made no tool calls";
    if (trace.some((t) => t.name === "run_recipe" && t.ok)) return "the run followed a recipe, so there was nothing new to learn";
    const topLevel = (t) => !String(t?.toolCallId || "").includes(":");
    const runIds = new Set(trace.map((t) => t.toolCallId));
    const fromThisRun = (c) => c?.source && (runIds.has(c.source.toolCallId) || [...runIds].some((id) => String(c.source.toolCallId || "").startsWith(`${id}`)));
    const claims = (graph?.claims || []).filter(fromThisRun);
    if (!claims.some((c) => c.status === "supported" && (c.kind === "attribution" || c.kind === "activity"))) {
        const kinds = [...new Set(claims.filter((c) => c.status === "supported").map((c) => c.kind))];
        return kinds.length ? `no supported attribution or activity claim (the run's supported claims were ${kinds.join(", ")})` : "no supported attribution or activity claim was made";
    }
    const isExpand = (t) => t.ok && topLevel(t) && (t.name === "expand" || t.name === "expand_all");
    let cutoff = trace.findIndex(isExpand);
    if (cutoff < 0) {
        const tried = trace.filter((t) => topLevel(t) && (t.name === "expand" || t.name === "expand_all")).length;
        if (tried) return `${tried === 1 ? "the expand" : `all ${tried} expands`} reported an error, so there was no anchor to build the plan around`;
        const cited = claims.flatMap((c) => (Array.isArray(c.evidence) ? c.evidence : [])).filter((k) => typeof k === "string" && graph?.nodes?.has(k));
        if (!cited.length) {
            const reads = trace.filter((t) => topLevel(t) && DIRECT_READ_TOOLS.test(t.name || "")).length;
            return `no expand ran${reads ? ` (the run read ${reads} trace${reads === 1 ? "" : "s"} directly instead)` : ""} and the supported claims cite no anchor the graph holds, so there was nothing to build the plan around`;
        }
        const firstClaimIdx = trace.findIndex((t) => t.ok && topLevel(t) && t.name === "claim");
        cutoff = firstClaimIdx >= 0 ? firstClaimIdx : trace.length;
    }
    const firstExpandIdx = cutoff;
    const before = trace.filter((t, i) => i < firstExpandIdx && t.ok && topLevel(t) && isQueryEntry(t));
    if (!before.length) return "every query came after the first expand; the plan is the queries that led to it";
    if (!before.some((t) => t.spec)) return "the queries before the expand were too large to keep";
    if (!before.some((t) => Array.isArray(getRows?.(t.toolCallId)) && getRows(t.toolCallId).length)) return "the queries before the expand kept no rows to trace the anchors back through";
    const chain = (() => {
        const queries = before.filter((t) => t.spec).map((t) => { const rows = getRows?.(t.toolCallId) || []; return Array.isArray(rows) && rows.length ? { entry: t, spec: t.spec, rows, index: indexRows(rows) } : null; }).filter(Boolean);
        const sinks = claims.flatMap((c) => (Array.isArray(c.evidence) ? c.evidence : [])).map((k) => String(k).slice(String(k).indexOf(":") + 1));
        return relevantQueries(queries, sinks);
    })();
    if (chain.length > MAX_PLAN_QUERIES) return `the queries that led to the answer are ${chain.length} deep; a plan keeps at most ${MAX_PLAN_QUERIES}`;
    return "no query's rows fed the expanded anchors or the claims, or nothing in the plan came from the question";
}

// Before a candidate is stored, the harness runs its query steps once more
// on the example and checks they re-materialize what the run expanded:
// every query returns rows and the expand step would pick at least one of
// the original anchors. No readers run, so the gate is cheap.
export async function replayRecipe({ candidate, graph, schema, analyticsTool, getRows = null, signal = null }) {
    if (!candidate?.recipe) return { ok: false, reason: "no candidate" };
    const state = await runRecipe({ recipe: candidate.recipe, params: candidate.example?.params || {}, question: candidate.example?.question || null, graph, schema, analyticsTool, expandRun: null, getRows, toolCallId: `replay-${candidate.recipe.id}`, signal, emitIntervalMs: 0, dryRun: true });
    if (state.phase !== "done") return { ok: false, reason: state.error || "replay failed", state };
    const queries = state.steps.filter((s) => s.kind === "search" || s.kind === "query");
    const failed = queries.find((s) => s.error || !s.rows);
    if (failed) return { ok: false, reason: failed.error ? `step failed: ${failed.error}` : `step ${queries.indexOf(failed) + 1} returned no rows`, state };
    const expand = state.steps.find((s) => s.kind === "expand");
    const expected = new Set(candidate.expected?.anchors || []);
    if (!expand) {
        // The step the plan converges on has to bring the cited anchors back;
        // a wide first step that happens to hold them does not make the plan.
        const fromN = Number(String(candidate.recipe.yields?.from || "").replace(/^s/, "")) || queries.length;
        const step = state.steps[fromN - 1] && state.steps[fromN - 1].kind !== "expand" ? state.steps[fromN - 1] : queries[queries.length - 1];
        const found = (step?.anchors || []).map((a) => a.key).filter((k) => expected.has(k));
        return found.length
            ? { ok: true, reproduced: [...new Set(found)], state }
            : { ok: false, reason: "the plan did not reproduce the anchors the answer cited", state };
    }
    const hit = (expand?.candidates || []).filter((k) => expected.has(k));
    if (hit.length) return { ok: true, reproduced: hit, state };
    // The distiller's source step did not surface the anchors on replay;
    // another kept step may (a narrower one, most often). Take the first
    // step, latest first, whose top anchors include one that was expanded,
    // and point the plan's expand at it.
    const kind = expand?.anchorKind || "session";
    const max = Math.max(1, Math.min(MAX_EXPAND_PER_RECIPE, Number(candidate.recipe.steps.find((s) => s.kind === "expand")?.anchors?.max) || MAX_EXPAND_PER_RECIPE));
    for (let i = queries.length - 1; i >= 0; i--) {
        const top = (queries[i].anchors || []).filter((a) => a.key.startsWith(`${kind}:`)).slice(0, max).map((a) => a.key);
        const found = top.filter((k) => expected.has(k));
        if (!found.length) continue;
        const stepIndex = state.steps.indexOf(queries[i]) + 1;
        const expandStep = candidate.recipe.steps.find((s) => s.kind === "expand");
        if (expandStep) expandStep.anchors = { ...expandStep.anchors, from: `s${stepIndex}` };
        return { ok: true, reproduced: found, state, sourceChanged: `s${stepIndex}` };
    }
    return { ok: false, reason: "the plan did not reproduce the anchors that were expanded", state };
}

// The task model writes what the deterministic distiller cannot: a
// one-line trigger for the class of question the plan answers and a
// short name, plus what each parameter means. The output is checked, and
// a trigger that carries the example's values, an id or a date is
// refused, so the fallback (the plan's shape) is never worse than a pasted
// scenario.
const DESCRIBE_TIMEOUT_MS = 20000;
const DESCRIBE_SYSTEM_PROMPT = [
    "You name reusable investigation plans for an analytics harness.",
    "You get the questions a plan has answered, the plan's steps, and its parameters with the values they took.",
    "Write `when`: one sentence, under 200 characters, describing the CLASS of question this plan answers in domain terms (who, what ran, where, when), so a future question of the same class can be recognized. Do not reuse the questions' phrasing. No names of people, machines or files, no ids, no dates or times, none of the example values.",
    "Write `examples`: two questions of the same class about different subjects, actions and machines than the ones given, each under 160 characters, with no names, ids, dates or times.",
    "Write `name`: three to seven words naming the plan.",
    "For each parameter write a `description` of the ROLE of what to pass, under 120 characters, general enough to fit every example.",
    "Return JSON only.",
].join("\n");
const DESCRIBE_OUTPUT_SCHEMA = Type.Object({
    name: Type.String(),
    when: Type.String(),
    examples: Type.Array(Type.String()),
    params: Type.Array(Type.Object({ name: Type.String(), description: Type.String() })),
});

function words(text) {
    return String(text || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

// A run of SHARED_PHRASE_WORDS words in common with a question is that
// question restated, not a class described.
export function sharesPhrase(a, b, n = SHARED_PHRASE_WORDS) {
    const wa = words(a);
    const wb = words(b);
    if (wa.length < n || wb.length < n) return false;
    const grams = new Set();
    for (let i = 0; i + n <= wb.length; i++) grams.add(wb.slice(i, i + n).join(" "));
    for (let i = 0; i + n <= wa.length; i++) if (grams.has(wa.slice(i, i + n).join(" "))) return true;
    return false;
}
const UUID_LIKE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}/i;
const DATE_LIKE = /\b20\d\d-\d\d-\d\d\b|\b\d{1,2}:\d\d(:\d\d)?\b/;

// A trigger is refused when it carries an id, a date or a time, a value
// from the question that looks like a name or an identifier (digits, or
// capitalized in the question), or every one of the example's plain-word
// values at once, which is the scenario restated. A common word that one
// parameter took ("lock") may appear: it describes the class.
export function acceptableTrigger(when, example, { maxLength = 200 } = {}) {
    const text = String(when || "").trim();
    if (text.length < 12 || text.length > maxLength) return false;
    if (UUID_LIKE.test(text) || DATE_LIKE.test(text)) return false;
    const lower = text.toLowerCase();
    const question = String(example?.question || "");
    const values = Object.values(example?.params || {}).filter((v) => typeof v === "string" && v.length >= 3);
    let plainHits = 0;
    for (const v of values) {
        if (!lower.includes(v.toLowerCase())) continue;
        const nameLike = /\d/.test(v) || (question.includes(v) && /^[A-Z]/.test(v) && v !== v.toLowerCase());
        if (nameLike) return false;
        plainHits++;
    }
    if (values.length && plainHits >= Math.max(2, values.length)) return false;
    return true;
}

export async function describeRecipeWithModel({ candidate, runSubAgent, timeoutMs = DESCRIBE_TIMEOUT_MS }) {
    const recipe = candidate?.recipe;
    if (!recipe) return null;
    const fallback = fallbackTrigger(recipe);
    const result = { ...recipe, name: fallback.name, when: fallback.when, described: false };
    if (typeof runSubAgent !== "function") return result;
    const examples = Array.isArray(recipe.examples) && recipe.examples.length ? recipe.examples : (candidate.example ? [candidate.example] : []);
    const questions = examples.map((e) => String(e?.question || "")).filter(Boolean);
    // A trigger or an example that restates a question the recipe has seen,
    // or carries one of their values, is refused.
    const general = (text) => text && examples.every((e) => acceptableTrigger(text, e)) && !questions.some((q) => sharesPhrase(text, q));
    let timer;
    try {
        const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error("describe timed out")), timeoutMs); });
        const task = JSON.stringify({
            questions: questions.map((q) => q.slice(0, 600)),
            steps: describeRecipe(recipe),
            params: (recipe.params || []).map((p) => ({ name: p.name, examples: examples.map((e) => e?.params?.[p.name] ?? null) })),
        });
        const out = await Promise.race([runSubAgent({ role: "task", task, systemPrompt: DESCRIBE_SYSTEM_PROMPT, tools: [], outputSchema: DESCRIBE_OUTPUT_SCHEMA }), timeout]);
        if (out?.status !== "done" || !out.result) return result;
        const name = String(out.result.name || "").trim();
        const when = String(out.result.when || "").trim();
        if (name.length >= 3 && name.length <= 60 && !UUID_LIKE.test(name)) result.name = name;
        if (general(when)) { result.when = when; result.described = true; } else if (when) { result.refusedWhen = when; }
        result.paraphrases = (Array.isArray(out.result.examples) ? out.result.examples : [])
            .map((e) => String(e || "").trim())
            .filter((e) => e.length >= 12 && e.length <= 160 && general(e))
            .slice(0, MAX_EXAMPLES);
        const descriptions = new Map((Array.isArray(out.result.params) ? out.result.params : []).map((p) => [String(p?.name || ""), String(p?.description || "").trim()]));
        result.params = (recipe.params || []).map((p) => {
            const d = descriptions.get(p.name);
            return d && d.length >= 3 && d.length <= 160 && !UUID_LIKE.test(d) ? { ...p, description: d } : p;
        });
    } catch { /* the fallback stands */ } finally { clearTimeout(timer); }
    return result;
}

// ── The save, decided by the model ───────────────────────────────────

// What the harness puts in front of the model when a run's plan passed
// replay: the plan in words, its parameters with the values they took, the
// question it answered and what it cost, the replay result, and a sample of
// what the store already holds. The model keeps it by calling save_recipe
// with a name and a class-level trigger, or declines in a sentence.
export const PROPOSAL_SAMPLE = 5;
export function recipeProposalText({ recipe, example, replay = null, existing = [] }) {
    const params = (recipe.params || []).map((p) => `${p.name}${p.required ? "" : " (optional)"}${example?.params?.[p.name] != null ? ` = ${JSON.stringify(example.params[p.name]).slice(0, 80)}` : ""}`).join("; ");
    const sample = (existing || []).slice(0, PROPOSAL_SAMPLE).map((r) => `  - "${r.name}"${r.source === "org" ? " (the org's)" : ""}: when ${r.when}`);
    const lines = [
        "<recipe_proposal>",
        `The harness distilled a plan from this run and replayed it on the question it answered${replay?.reproduced?.length ? ` (it brought back ${replay.reproduced.length} of the anchors the answer rested on)` : ""}. Decide whether it is a recipe worth keeping.`,
        `id: ${recipe.id}`,
        `plan: ${describeRecipe(recipe)}`,
        `parameters: ${params || "none"}`,
        `answered: ${JSON.stringify(String(example?.question || "").slice(0, 300))}`,
        `cost of this run: ${Number.isFinite(recipe.origin?.calls) ? `${recipe.origin.calls} tool calls` : "unknown"}`,
        sample.length ? `recipes the store already holds:\n${sample.join("\n")}` : "the store holds no recipes yet",
        "",
        "Keep it if a future question of the same class (other subjects, actions, machines, times) would be answered by these steps, and it does not duplicate a recipe above. To keep it, call save_recipe with the id, a short name, and `when`: one sentence naming the CLASS of question in domain terms, with no names, ids, dates, times or values from this question. To decline, reply in one sentence with the reason and do not call the tool.",
        "</recipe_proposal>",
    ];
    return lines.join("\n");
}

// The tool that keeps a proposed plan. It exists only for plans the harness
// has proposed in this chat (held in `pending` by id); the model's name and
// trigger are taken when they pass the same checks the describer's output
// passes, and the plan's shape stands in when they do not.
export function createSaveRecipeTool({ store, pending, persist = () => {} }) {
    return {
        name: "save_recipe",
        label: "Save recipe",
        description: "Keep a plan the harness proposed in a <recipe_proposal> block, by its id. Give it a short name and `when`, one sentence naming the class of question it answers in domain terms (who, what ran, where, when), with no names, ids, dates, times or values from the question. Only proposed ids can be saved.",
        parameters: Type.Object({
            id: Type.String({ description: "The proposal's id." }),
            name: Type.Optional(Type.String({ description: "A short name, under 80 characters." })),
            when: Type.Optional(Type.String({ description: "One sentence: the class of question this plan answers." })),
        }),
        async execute(toolCallId, args) {
            const id = String(args?.id || "").trim();
            const proposed = pending?.get?.(id);
            if (!proposed) {
                const ids = pending ? [...pending.keys()] : [];
                return { content: [{ type: "text", text: ids.length ? `no proposed recipe ${JSON.stringify(id)}; proposed: ${ids.join(", ")}` : "no recipe has been proposed in this chat; the harness proposes one at the end of a run whose plan replays" }], details: { kind: "save_recipe", error: "unknown proposal", id }, isError: true };
            }
            const { recipe, example } = proposed;
            const notes = [];
            const name = typeof args?.name === "string" && args.name.trim().length >= 3 && args.name.trim().length <= 80 ? args.name.trim() : null;
            if (args?.name && !name) notes.push("name kept as the plan's: yours was empty or over 80 characters");
            const examples = Array.isArray(recipe.examples) && recipe.examples.length ? recipe.examples : [example];
            const when = typeof args?.when === "string" ? args.when.trim() : "";
            // The model's trigger may run long and may share the question's
            // words (they are the class's words); it may not carry the
            // question's values, ids or dates, nor restate the question.
            const general = when && examples.every((e) => acceptableTrigger(when, e, { maxLength: 300 })) && !examples.some((e) => e?.question && sharesPhrase(when, String(e.question), 8));
            if (when && !general) notes.push("trigger kept as the plan's shape: yours carried this question's values, an id or a date, restated the question, or was not one sentence under 300 characters");
            const toStore = { ...recipe, ...(name ? { name } : {}), ...(general ? { when, described: true } : {}) };
            const stored = store.upsert(toStore);
            if (stored?.error) {
                // The proposal stays pending, marked with the failure, so the
                // run end reports a save that failed rather than a decline.
                pending.set(id, { ...proposed, error: stored.error });
                try { store.noteSettle?.({ learned: null, reason: `the model kept the plan but it could not be saved: ${stored.error}` }); } catch { /* optional */ }
                return { content: [{ type: "text", text: `could not save: ${stored.error}` }], details: { kind: "save_recipe", error: stored.error, id }, isError: true };
            }
            pending.delete(id);
            const saved = store.get?.(stored.id) || toStore;
            try { store.noteSettle?.({ learned: saved.name, merged: stored.matched === "stored", reason: null }); } catch { /* optional */ }
            persist();
            const outcome = stored.matched === "stored" ? "generalized an existing recipe" : stored.matched === "org" ? "adopted the org's recipe" : "new recipe";
            // The result carries the whole recipe as JSON: the org's trace store
            // keeps it as saved, to be read back without re-distilling.
            const text = JSON.stringify({ saved, stored, outcome, notes });
            return { content: [{ type: "text", text }], details: { kind: "save_recipe", recipe: saved, stored, outcome, notes } };
        },
    };
}

// What the run end says about a recipe once the model's turn on a proposal
// is over: kept (the id left pending), kept but not saved (the id stayed
// with an error), or declined (the id stayed, and the model's last words are
// the reason). Pure, so the branching is tested without a chat.
export function proposalOutcome({ proposed, pending, store, lastText = "" }) {
    if (!proposed?.id) return null;
    const left = pending?.get?.(proposed.id) || null;
    if (left?.error) {
        pending.delete(proposed.id);
        return { kind: "failed", note: `Recipe: the model kept the plan but it could not be saved`, detail: String(left.error), reason: `the model kept the plan but it could not be saved: ${left.error}` };
    }
    if (left) {
        pending.delete(proposed.id);
        const said = String(lastText || "").trim().slice(0, 300);
        return { kind: "declined", note: "Recipe: the model declined the proposed plan", detail: said || null, reason: `the model declined the proposed plan${said ? `: ${said.slice(0, 200)}` : ""}` };
    }
    const saved = store?.list?.({ includeArchived: true })?.find((r) => r.id === proposed.id) || null;
    return { kind: "kept", note: `Recipe: kept as "${saved?.name || proposed.name}"`, detail: saved?.when || null, reason: null };
}

// A proposal the model never got to decide on: the chat moved on (or never
// freed up) before its turn. The plan is dropped, the harness turn reset, and
// the store's note and the status row say so.
export function dropProposal({ recipe, why, pending, harnessTurn, store, note = () => {} }) {
    pending?.delete?.(recipe.id);
    if (harnessTurn) { harnessTurn.active = false; harnessTurn.proposed = null; }
    const reason = `a plan was proposed but the chat moved on before the model could decide (${why})`;
    try { store?.noteSettle?.({ learned: null, reason }); } catch { /* optional */ }
    note("recipe-settle", "Recipe: not decided, the chat moved on", describeRecipe(recipe), { lock: false });
    return reason;
}


// ── What happens to recipes when a run ends ──────────────────────────

// How a run of a recipe is judged, by what the model's supported claims in
// this run rest on, strongest first:
//   cited: a claim cites an anchor the recipe returned (or the recipe's own
//          reader made such a claim);
//   near:  a claim cites a node one hop from one of those anchors, the
//          session's process or the endpoint's session, which is how the
//          model usually cites what it went on to read;
//   saved: the run made a supported claim and came in under the recipe's
//          origin cost on a comparable question.
// A run that cites nothing the recipe returned on a question with no cost
// to compare against fails, and so does one that holds its claims but does
// not shorten the investigation. The reason is recorded with the run.
export function judgeRecipeRun({ graph, runIds, anchors = [], claimSuccess = null, saved = null }) {
    const ids = runIds instanceof Set ? runIds : new Set(runIds || []);
    const claims = (graph?.claims || []).filter((c) => c.status === "supported" && c.bearing !== "context" && c.source && ids.has(c.source.toolCallId));
    const evidenceOf = (c) => (Array.isArray(c.evidence) ? c.evidence : []);
    const wanted = new Set(anchors || []);
    const near = new Set();
    for (const e of graph?.edges?.values?.() || []) {
        if (wanted.has(e.from)) near.add(e.to);
        if (wanted.has(e.to)) near.add(e.from);
    }
    let basis = null;
    if (claimSuccess === true || claims.some((c) => evidenceOf(c).some((k) => wanted.has(k)))) basis = "cited";
    else if (claims.some((c) => evidenceOf(c).some((k) => near.has(k)))) basis = "near";
    else if (claims.length && saved != null && saved > 0) basis = "saved";
    const claimed = claimSuccess === true || claims.length > 0;
    const success = Boolean(basis) && (saved == null || saved > 0);
    let reason = null;
    if (!success) {
        if (!claimed) reason = "no supported claim in the run";
        else if (basis) reason = "the run did not come in under the recipe's origin cost";
        else if (saved == null) reason = "the claims cite nothing the recipe returned, and the question has no cost to compare against";
        else reason = "the claims cite nothing the recipe returned, and the run did not come in under the recipe's origin cost";
    }
    return { success, basis: success ? basis : null, reason, claimed };
}

// Recipes that ran this turn are judged against the whole run's cost, and
// the run itself may become a recipe: distil, replay, describe, store, and
// describe again when it merged into one the store already had. Pure in
// its inputs so the chat can be tested without an agent.
export async function settleRecipeRun({ calls = null, trace, question, graph, getRows = null, store, ran = [], replay = null, describe = null, propose = null, onPhase = null, analyticsTool = null, schema = null, log = () => {} }) {
    const out = { rootCalls: 0, outcomes: [], candidate: false, replay: null, stored: null, redescribed: false, refusedWhen: null };
    if (!store) return out;
    const topLevel = (t) => !String(t?.toolCallId || "").includes(":");
    out.rootCalls = Number.isFinite(calls) ? calls : (trace || []).filter(topLevel).length;
    // One use per recipe per turn: a recipe the model called twice in a
    // turn (a retry after a bad parameter) was counted as two failures.
    const runIds = new Set((trace || []).map((t) => t.toolCallId));
    const byRecipe = new Map();
    for (const run of ran || []) {
        if (!run?.id) continue;
        const e = byRecipe.get(run.id) || { id: run.id, claimSuccess: false, anchors: [], origin: run.origin || null, calls: 0 };
        e.claimSuccess = e.claimSuccess || run.claimSuccess === true;
        for (const k of run.anchors || []) if (!e.anchors.includes(k)) e.anchors.push(k);
        e.calls += 1;
        byRecipe.set(run.id, e);
    }
    // The origin cost belongs to the question the recipe was learned on. A
    // recipe that fires on a different question has no cold cost to be
    // measured against, so its saving is unknown and it is judged on its
    // claims alone; only a run whose question shares a phrase with one the
    // recipe has answered is scored for savings.
    const questionKnown = (id) => {
        const stored = store.get?.(id);
        const examples = Array.isArray(stored?.examples) ? stored.examples : (stored?.example ? [stored.example] : []);
        return examples.some((e) => e?.question && sharesPhrase(String(question || ""), String(e.question)));
    };
    for (const run of byRecipe.values()) {
        const origin = Number.isFinite(run.origin?.calls) ? run.origin.calls : null;
        const saved = origin == null || !questionKnown(run.id) ? null : origin - out.rootCalls;
        const { success, basis, reason } = judgeRecipeRun({ graph, runIds, anchors: run.anchors, claimSuccess: run.claimSuccess, saved });
        try { store.recordUse(run.id, { success, calls: out.rootCalls, saved, basis, reason }); } catch (err) { log("recipe outcome not recorded:", err); }
        out.outcomes.push({ id: run.id, success, saved, calls: out.rootCalls, runs: run.calls, basis, reason });
    }
    const candidate = distillRecipeFromRun({ trace, question, graph, getRows, calls });
    if (!candidate) {
        out.reason = whyNoRecipe({ trace, graph, getRows });
        try { store.noteSettle?.({ learned: null, reason: out.reason }); } catch { /* optional */ }
        return out;
    }
    out.candidate = true;
    const replayFn = replay || ((c) => replayRecipe({ candidate: c, graph, schema, analyticsTool, getRows }));
    try { onPhase?.("replaying", { plan: describeRecipe(candidate.recipe) }); } catch { /* the host's failure is its own */ }
    const result = await replayFn(candidate);
    out.replay = { ok: Boolean(result?.ok), reason: result?.reason || null, sourceChanged: result?.sourceChanged || null };
    if (!result?.ok) {
        const steps = (result?.state?.steps || []).map((st) => `${st.kind} ${st.entity || st.anchorKind || ""}: ${st.error ? `error ${String(st.error).slice(0, 80)}` : st.kind === "expand" ? `${(st.candidates || []).length} candidates` : `${st.rows} rows`}`);
        log("recipe not stored, replay:", result?.reason, "| plan:", describeRecipe(candidate.recipe), "| example:", JSON.stringify(candidate.example?.params || {}).slice(0, 300), "| steps:", steps.join("; "));
        out.reason = `a plan was found (${describeRecipe(candidate.recipe)}) but its replay was refused: ${result?.reason}`;
        try { store.noteSettle?.({ learned: null, reason: out.reason }); } catch { /* optional */ }
        return out;
    }
    // With a proposer, the decision is the model's: the candidate is handed
    // over with its example and the store's sample, and nothing is stored
    // here. The save_recipe tool stores it if the model calls it.
    if (typeof propose === "function") {
        const recipe = candidate.recipe;
        out.proposed = { id: recipe.id, name: recipe.name };
        try { store.noteSettle?.({ learned: null, reason: `a plan was proposed to the model (${describeRecipe(recipe)}); waiting for its decision` }); } catch { /* optional */ }
        try { await propose({ recipe, example: candidate.example, expected: candidate.expected, replay: out.replay, existing: store.list().slice(0, PROPOSAL_SAMPLE) }); } catch (err) { log("recipe proposal failed:", err); }
        return out;
    }
    const describeFn = describe || ((c) => describeRecipeWithModel({ candidate: c, runSubAgent: null }));
    const recipe = await describeFn(candidate);
    if (!recipe) return out;
    if (recipe.refusedWhen) { out.refusedWhen = recipe.refusedWhen; log("recipe trigger refused, plan shape used instead:", recipe.refusedWhen); delete recipe.refusedWhen; }
    const stored = store.upsert(recipe);
    out.stored = stored;
    if (stored?.error) {
        out.reason = `a plan was learned (${recipe.name}) but could not be saved: ${stored.error}`;
        log("recipe not saved:", stored.error);
        try { store.noteSettle?.({ learned: null, reason: out.reason }); } catch { /* optional */ }
        return out;
    }
    try { store.noteSettle?.({ learned: recipe.name, merged: stored?.matched === "stored", reason: null }); } catch { /* optional */ }
    // A second run on the same path: the merged plan and its examples get
    // described again, so the trigger is written for the class the recipe
    // has actually seen.
    if (stored?.matched === "stored" && (stored.generalized || stored.newExample)) {
        const merged = store.get(stored.id);
        const again = merged ? await describeFn({ recipe: merged, example: merged.examples?.[merged.examples.length - 1] || merged.example }) : null;
        if (again?.described) {
            store.describe(stored.id, { name: again.name, when: again.when, params: again.params, paraphrases: again.paraphrases });
            out.redescribed = true;
        }
    }
    return out;
}

// ── The record a run publishes ───────────────────────────────────────

// Verdicts are the browser's: the run end judges a recipe run and records
// it in this browser's store. To reach the org they ride on something the
// chat already writes into its own transcript: every run_recipe result
// ends with this browser's record of the recipe as it stood before the run
// (uses, successes, average saved, the last verdict's basis), and the trace
// store indexes the result like any other tool result. Other browsers read
// the records back with the same search that reads saves and weigh the
// org's record beside their own. One run stale by construction.
export const RECIPE_RECORD_TAG = "recipe_record";
const RECIPE_RECORD = /<recipe_record>(\{[^\n]*?\})<\/recipe_record>/;

export function recipeRecordLine(recipe, store, now = Date.now()) {
    const browser = store?.browserId?.();
    if (!browser || !recipe?.id) return "";
    const view = store.get?.(recipe.id) || recipe;
    const record = {
        id: recipe.id,
        browser,
        uses: view.uses || 0,
        successes: view.successes || 0,
        savedAvg: Number.isFinite(view.savedAvg) ? Math.round(view.savedAvg * 10) / 10 : null,
        last: view.lastRun ? (view.lastRun.success ? view.lastRun.basis || "success" : "failed") : null,
        at: now,
    };
    return `<${RECIPE_RECORD_TAG}>${JSON.stringify(record)}</${RECIPE_RECORD_TAG}>`;
}

// The record in a run_recipe result's text, or null when there is none or
// it does not read as one.
export function recipeRecordFromText(text) {
    const m = RECIPE_RECORD.exec(String(text || ""));
    if (!m) return null;
    try {
        const r = JSON.parse(m[1]);
        if (!r || typeof r.id !== "string" || typeof r.browser !== "string") return null;
        return {
            id: r.id,
            browser: r.browser,
            uses: Number.isFinite(r.uses) ? r.uses : 0,
            successes: Number.isFinite(r.successes) ? r.successes : 0,
            savedAvg: Number.isFinite(r.savedAvg) ? r.savedAvg : null,
            last: typeof r.last === "string" ? r.last : null,
            at: Number.isFinite(r.at) ? r.at : null,
        };
    } catch { return null; }
}

// ── The prompt block and the tool ────────────────────────────────────

export function recipesPromptSection(recipes, { limit = 6 } = {}) {
    const list = (recipes || []).filter((r) => !r.stale && (r.status || "active") === "active").slice(0, limit);
    if (!list.length) return "";
    const lines = ["<harness_recipes>", "Recipes the harness runs end to end, most trusted first. Call run_recipe { name, params, question } and read the rows, anchors and claims it returns instead of writing the queries yourself:"];
    for (const r of list) {
        const local = r.uses ? `${r.uses} run${r.uses === 1 ? "" : "s"}, ${r.successes || 0} with supported claims` : "not run yet";
        const org = r.orgUses ? `; ${r.orgUses} run${r.orgUses === 1 ? "" : "s"} across the org, ${r.orgSuccesses || 0} with supported claims` : "";
        const usage = `${local}${org}`;
        const saved = Number.isFinite(r.savedAvg) ? `, ${r.savedAvg >= 0 ? "saves" : "costs"} ${Math.abs(r.savedAvg).toFixed(0)} tool call${Math.abs(r.savedAvg).toFixed(0) === "1" ? "" : "s"} on average` : "";
        const confidence = Number.isFinite(r.confidence) ? `confidence ${r.confidence.toFixed(2)}; ` : "";
        const params = (r.params || []).map((p) => `${p.name}${p.required ? " (required)" : " (optional)"}: ${p.description}`).join("; ");
        const also = Array.isArray(r.paraphrases) && r.paraphrases.length ? ` Also: "${r.paraphrases[0]}".` : "";
        lines.push(`  - "${r.name}" [${r.source}; ${confidence}${usage}${saved}]: when ${r.when}.${also} Params: ${params}. Steps: ${describeRecipe(r)}.`);
    }
    lines.push("</harness_recipes>");
    return lines.join("\n");
}

const RECIPES_BLOCK = /\n*<harness_recipes>[\s\S]*?<\/harness_recipes>/;
export function replaceRecipesBlock(systemPrompt, section) {
    const prompt = String(systemPrompt || "");
    const clean = section ? `\n\n${section}` : "";
    if (RECIPES_BLOCK.test(prompt)) return prompt.replace(RECIPES_BLOCK, clean);
    return clean ? prompt + clean : prompt;
}

export function createRecipeTools({ graph, getSchema, analyticsTool, expandRun, store, persist = () => {}, getRows = null, emitIntervalMs = 150, onRecipeRun = null, pending = null }) {
    const runTool = {
        name: "run_recipe",
        label: "Run recipe",
        description:
            "Only for a recipe the harness_recipes block of your instructions lists; when there is no such block, none has been learned yet and you investigate with your own queries and expand. Run a named recipe end to end in the harness: the queries of a plan that worked before, in order, with your values filled in and each step's rows feeding the next, then expand over the anchors the plan found, each with a reader that returns claims. One call replaces the queries and the sub-agents. The recipes available, their trigger and their parameters are listed in the harness_recipes block; pass the recipe name, the params it lists (and a window when you know the moment), and your question.",
        parameters: Type.Object({
            name: Type.String({ description: "Recipe name or id from the harness_recipes block." }),
            params: Type.Optional(Type.Object({
                term: Type.Optional(Type.String({ description: "Text to look for, as the recipe's params describe." })),
                window: Type.Optional(Type.Unknown({ description: "{ start, end } in RFC 3339 UTC, or a string like \"7d\"." })),
            }, { additionalProperties: true })),
            question: Type.Optional(Type.String({ description: "What to establish; the readers answer it with claims." })),
        }),
        async execute(toolCallId, args, signal, onUpdate) {
            const wanted = String(args?.name || "").trim().toLowerCase();
            const recipe = store.list().find((r) => r.id.toLowerCase() === wanted || r.name.toLowerCase() === wanted);
            if (!recipe) {
                const names = store.list().map((r) => `"${r.name}"`).join(", ");
                const text = names
                    ? `no recipe named ${JSON.stringify(args?.name)}; available: ${names}`
                    : "no recipes have been learned yet; investigate with your own queries and expand, and a run that queries, expands and ends in a supported claim becomes one";
                return { content: [{ type: "text", text }], details: { kind: "recipe", phase: "error", error: names ? "unknown recipe" : "no recipes yet", name: args?.name || null, steps: [], claims: [] } };
            }
            const missing = (recipe.params || []).filter((p) => p.required && (args?.params?.[p.name] == null || args.params[p.name] === ""));
            if (missing.length) {
                return { content: [{ type: "text", text: `recipe "${recipe.name}" needs ${missing.map((p) => `${p.name} (${p.description})`).join("; ")}` }], details: { kind: "recipe", phase: "error", error: `missing ${missing.map((p) => p.name).join(", ")}`, name: recipe.name, id: recipe.id, steps: [], claims: [] } };
            }
            const schema = getSchema?.();
            const state = await runRecipe({ recipe, params: args?.params || {}, question: args?.question || null, graph, schema, analyticsTool, expandRun, getRows, toolCallId, signal, onUpdate, emitIntervalMs });
            // A success is a supported claim that answers the question and
            // cites the anchor this run expanded to make it, not a supported
            // claim a reader made about something else it saw there.
            const expandStep = (recipe.steps || []).some((st) => st.kind === "expand");
            const anchors = [...new Set(state.steps.flatMap((st) => (st.anchors || []).map((a) => a.key)))];
            // A query-only plan makes no claims of its own: whether the model's
            // later claims cite the anchors it returned is judged at run end.
            const claimSuccess = expandStep
                ? state.claims.some((c) => c.status === "supported" && c.bearing !== "context" && (!Array.isArray(c.evidence) || c.evidence.includes(c.anchor)))
                : null;
            // With a collector the outcome is judged at run end, against what
            // the whole run cost; without one it is judged here on the claims.
            if (typeof onRecipeRun === "function") onRecipeRun({ id: recipe.id, claimSuccess, anchors, origin: recipe.origin || null });
            else { try { store.recordUse(recipe.id, { success: Boolean(claimSuccess) }); } catch { /* the store is optional */ } }
            persist();
            // The record this browser holds of the recipe, as it stood before
            // this run, rides on the result so the org can read it back.
            const record = recipeRecordLine(recipe, store);
            return { content: [{ type: "text", text: record ? `${recipeSummary(state)}\n${record}` : recipeSummary(state) }], details: { ...state, graph: graphSnapshot(graph, { newKeys: state.newKeys || [] }) } };
        },
    };
    return pending ? [runTool, createSaveRecipeTool({ store, pending, persist })] : [runTool];
}
